/**
 * What a person may read of a database error.
 *
 * Our functions refuse with sentences written for the person ("that pairing
 * code is not valid, or has expired"), raised with one of a few codes and
 * nothing attached. Anything else is Postgres speaking: a broken rule (`new
 * row for relation … violates check constraint …`, with the failing row in its
 * details), a missing function, a timeout. That is never shown: the person gets
 * a plain sentence, and the detail stays in the database's own log.
 */
export interface DatabaseError {
    code?: string | null;
    message?: string | null;
    details?: string | null;
    hint?: string | null;
}

/** The codes our functions raise their sentences with (`raise exception … using errcode = …`). */
const OUR_REFUSAL_CODES = new Set([
    '42501', // insufficient_privilege
    'P0001', // raise_exception (no code given)
    'P0002', // no_data_found
    '23514', // check_violation
    '23505', // unique_violation
    '23503', // foreign_key_violation
    '23001', // restrict_violation
    '22023', // invalid_parameter_value
    '22007', // invalid_datetime_format
    '55P03', // lock_not_available
    '40001', // serialization_failure
    '53300', // too_many_connections (our rate limits)
    'PT403', // refused
    'PT409', // a replay, refused
    '28000', // invalid_authorization_specification
]);

/** Words of Postgres's own messages, never of ours. */
const POSTGRES_WORDING = /\b(relation|constraint|violates|column|syntax|duplicate key|null value|operator|schema|failing row|permission denied for)\b|"|\bfunction\s+\S+\(/i;

export const GENERIC_REFUSAL = 'That could not be done just now. Nothing was changed. Try again, or contact Timely support if it keeps happening.';

export function userFacingRefusal(error: DatabaseError | null | undefined, fallback: string = GENERIC_REFUSAL): string {
    const message = (error?.message ?? '').trim();
    const ours = message.length > 0 && message.length <= 300
        && OUR_REFUSAL_CODES.has(error?.code ?? '')
        && !error?.details && !error?.hint
        && !POSTGRES_WORDING.test(message);
    return ours ? message : fallback;
}
